/**
 * Custom hook para lógica de sección y navegación
 * Centraliza la lógica de routing y validación de secciones
 */
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { Language, UseSectionReturn, GoToFunction } from "../types";

// Secciones principales válidas
const VALID_MAIN_SECTIONS: string[] = ["home", "quienes-somos", "capitulos", "opiniones", "privacidad", "suscripcion"];
const VALID_CHAPTER_SUBS: string[] = ["opciones", "comic", "articulos", "extras"];

/**
 * Hook para obtener información de la sección actual
 */
export const useSection = (): UseSectionReturn => {
    const location = useLocation();
    const navigate = useNavigate();
    const { i18n } = useTranslation();

    // Derivar la sección actual a partir de la URL
    const pathParts = location.pathname.split("/").filter(Boolean);
    let currentSection = "";

    if (pathParts.length === 0) {
        currentSection = "home";
    } else if (pathParts[0] === "quienes-somos") {
        currentSection = "quienes-somos";
    } else if (pathParts[0] === "capitulos") {
        currentSection = "capitulos";
    } else if (pathParts[0] === "capitulo") {
        currentSection = pathParts.join("-");
    } else if (pathParts[0] === "opiniones") {
        currentSection = "opiniones";
    } else if (pathParts[0] === "privacidad") {
        currentSection = "privacidad";
    } else if (pathParts[0] === "suscripcion") {
        currentSection = "suscripcion";
    }

    // Validación de secciones
    const isValidChapterSection =
        currentSection.startsWith("capitulo-") &&
        VALID_CHAPTER_SUBS.some((suf) => currentSection.endsWith(suf));
    const isSectionValid = VALID_MAIN_SECTIONS.includes(currentSection) || isValidChapterSection;

    // Detectar tipo de sección
    const isSliderActive = currentSection.endsWith("-comic") || currentSection.endsWith("-extras");
    const isChapterOptions = currentSection.endsWith("-opciones");
    const isComicView = currentSection.endsWith("-comic");
    const isArticlesView = currentSection.endsWith("-articulos");
    const isExtrasView = currentSection.endsWith("-extras");

    // Idioma actual
    const currentLanguage: Language = i18n.language.startsWith("es") ? "español" : "ingles";

    // Función de navegación
    const goTo: GoToFunction = (section: string): void => {
        if (VALID_MAIN_SECTIONS.includes(section)) {
            navigate(section === "home" ? "/" : `/${section}`);
        } else {
            const parts = section.split("-");
            const chapterIdInternal = parts.slice(0, 2).join("-");
            const chapterIdForURL = chapterIdInternal.replace("chapter", "capitulo");
            const sub = parts.slice(2).join("-");
            navigate(`/capitulo/${chapterIdForURL}/${sub}`);
        }
    };

    return {
        currentSection,
        isSectionValid,
        isSliderActive,
        isChapterOptions,
        isComicView,
        isArticlesView,
        isExtrasView,
        currentLanguage,
        goTo,
        goBack: () => navigate(-1),
    };
};

export default useSection;
